import { useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  AlertDescription,
  FormControl,
  FormLabel,
  Textarea,
} from '@chakra-ui/react';

import Card from 'components/card/Card';

import { apiGetSpecialtyFormData } from 'api';

import { useAppSelector, useAppDispatch } from 'hooks';
import {
  initializeForm,
  resetForm,
  setLineOfBusiness,
  setNotesToUnderwriter,
} from 'reducers/specialtyFormReducer';
import { useSpecialtyCarriers } from 'hooks/index';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import {
  WIP_STATUS_BINDER_PENDING_UPLOAD,
  ROLE_ADMIN,
} from 'variables/constants';
import { AuthContext } from 'contexts/AuthContext';

import SpecialtyPolicyInformation from './sections/SpecialtyPolicyInformation';
import SpecialtyDeclarationUpload from './sections/SpecialtyDeclarationUpload';
import SpecialtyLOAUpload from './sections/SpecialtyLOAUpload';
import SpecialtyMVRUpload from './sections/SpecialtyMVRUpload';
import SpecialtyDashReport from './sections/SpecialtyDashReport';
import SpecialtySubmit from './sections/SpecialtySubmit';
import DisclosureAndAttestation from 'views/shared/DisclosureAndAttestation';
import { APISpecialtyForm } from 'types/api.types';

const SpecialtyFormView = () => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const isDataSet = useAppSelector((state) => state.specialtyForm.isDataSet);
  const insuranceCompany = useAppSelector(
    (state) => state.specialtyForm.data.insurance_company,
  );
  const wipStatus = useAppSelector((state) => state.specialtyForm.wipStatus);
  const lineOfBusiness = useAppSelector(
    (state) => state.specialtyForm.lineOfBusiness,
  );
  const isLOA = useAppSelector(
    (state) => state.specialtyForm.data.policy_information.is_loa,
  );
  const notesToUnderwriter = useAppSelector(
    (state) => state.specialtyForm.data.notes_to_underwriter || '',
  );
  const isSpecialtyUwNotesEnabled = useFeatureFlag('enable-specialty-uw-notes');

  const { availableCarriers } = useSpecialtyCarriers(
    lineOfBusiness || undefined,
  );

  // Check if form is locked (completed)
  const isLocked = wipStatus === WIP_STATUS_BINDER_PENDING_UPLOAD;
  const isAdmin = user?.role === ROLE_ADMIN;
  const canEditPremium = isAdmin && isLocked;

  // Get the selected carrier's info message
  const selectedCarrierConfig = availableCarriers.find(
    (c) => c.name === insuranceCompany,
  );
  const carrierInfoMessage = selectedCarrierConfig?.infoMessage;

  const { data: initialData, isFetching } = useQuery({
    queryKey: ['specialty_form', uuid],
    queryFn: () => apiGetSpecialtyFormData(uuid!),
    refetchOnWindowFocus: false,
  }) as {
    error: any;
    data: APISpecialtyForm;
    isFetching: boolean;
  };

  useEffect(() => {
    dispatch(resetForm());
  }, [uuid, dispatch]);

  useEffect(() => {
    if (isDataSet || isFetching || !initialData) return;

    dispatch(initializeForm(initialData));

    if (initialData.line_of_business) {
      dispatch(
        setLineOfBusiness(initialData.line_of_business as 'Auto' | 'Home'),
      );
    }
  }, [initialData, uuid, isDataSet, isFetching, dispatch]);

  if (!isDataSet) {
    return null;
  }

  return (
    <>
      {isLocked && isAdmin && (
        <Alert status="warning" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been submitted. As an admin, you may only edit the
              premium field. All other fields are locked.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      {isLocked && !isAdmin && (
        <Alert status="info" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>
              This form has been completed and submitted. No further changes can
              be made.
            </strong>
          </AlertDescription>
        </Alert>
      )}
      <SpecialtyPolicyInformation
        isLocked={isLocked}
        canEditPremium={canEditPremium}
      />

      {/* Show LOA uploads for LOA submissions, Declaration for regular */}
      {isLOA ? (
        <SpecialtyLOAUpload isLocked={isLocked} />
      ) : (
        <SpecialtyDeclarationUpload isLocked={isLocked} />
      )}

      {/* Only show carrier info message for Auto when applicable (non-LOA) */}
      {carrierInfoMessage && lineOfBusiness === 'Auto' && !isLOA && (
        <Alert status="info" borderRadius="md" mb="20px">
          <AlertIcon />
          <AlertDescription>
            <strong>{carrierInfoMessage}</strong>
          </AlertDescription>
        </Alert>
      )}

      {/* Show MVR and DASH sections for Auto (non-LOA) or Aviva Elite LOA */}
      {lineOfBusiness === 'Auto' &&
        (!isLOA || insuranceCompany === 'Aviva Elite') && (
          <>
            <SpecialtyMVRUpload isLocked={isLocked} />
            <SpecialtyDashReport isLocked={isLocked} />
          </>
        )}

      <DisclosureAndAttestation
        formStateKey="specialtyForm"
        isLocked={isLocked}
      />
      {isSpecialtyUwNotesEnabled && (
        <Card mb="20px">
          <FormControl>
            <FormLabel fontWeight="bold" fontSize="lg" mb={4}>
              Notes for BIG Underwriting
            </FormLabel>
            <Textarea
              value={notesToUnderwriter}
              onChange={(e) => dispatch(setNotesToUnderwriter(e.target.value))}
              placeholder="These notes will only be visible in the Jira ticket and will not appear on any client-facing documents"
              isDisabled={isLocked}
              rows={4}
            />
          </FormControl>
        </Card>
      )}
      <SpecialtySubmit isLocked={isLocked} canEditPremium={canEditPremium} />
    </>
  );
};

export default SpecialtyFormView;
